/**
 * Main JS functionality
 * Dependencies: jQuery, anime.js (ES module)
 */
import { animate, stagger } from 'animejs';
import { debounce } from './resizeHandler.js';

jQuery(document).ready(function() {
  
  /*************************************
   * CONFIG & COMMON VARIABLES
   *************************************/
  const mobileWidth = 762; // breakpoint for mobile screens

  // Cached jQuery and DOM Elements
  const $window = jQuery(window);
  const $document = jQuery(document);
  const headerEl = document.querySelector('header.site-header');
  const sticky = document.querySelectorAll('.sticky');
  const tabsContainer = document.querySelector('.tabs');
  const workAccordions = document.querySelectorAll('.our-work-accordion');
  const tabs = document.querySelectorAll('[role="tab"]');
  const toggleAllSwitches = document.querySelectorAll('.toggle-all');
  const ourExpertsTabs = document.querySelectorAll('.our-experts-tabs [role="tab"]');
  let bannerSleeveHeight = 0;

  /*************************************
   * STICKY HEADER & ANIMATION
   *************************************/
  if (headerEl) {
    const headerSleeve = headerEl.querySelector('.banner-sleeve');
    //set bannerSleeveHeight declared earlier;
    bannerSleeveHeight = document.querySelector('.banner-sleeve').offsetHeight;
    const adminBar = document.getElementById('wpadminbar');
    let lastScroll = window.scrollY;
    let ticking = false;

    // State variables for header background
    let headerBgIsWhite = false;
    let bgAnimation = null;

    // Animate header sleeve background to white
    const fadeHeaderSleeveToWhite = () => {
      if (headerBgIsWhite) return;
      headerBgIsWhite = true;
      if (bgAnimation) bgAnimation.cancel();
      bgAnimation = headerSleeve.animate([
        { backgroundColor: "rgba(246, 246, 239, 1)" },
        { backgroundColor: "rgba(255, 255, 255, 1)" }
      ], { duration: 300, fill: "forwards" });
    };

    // Animate header sleeve background to transparent
    const fadeHeaderSleeveToTransparent = () => {
      if (!headerBgIsWhite) return;
      headerBgIsWhite = false;
      if (bgAnimation) bgAnimation.cancel();
      bgAnimation = headerSleeve.animate([
        { backgroundColor: "rgba(255, 255, 255, 1)" },
        { backgroundColor: "rgba(246, 246, 239, 1)" }
      ], { duration: 300, fill: "forwards" });
    };

    const updateHeader = () => {
      const currentScroll = window.scrollY;
      const headerTrigger = headerSleeve.offsetHeight + headerEl.offsetTop;

      // At the very top: reset header and fade out background.
      if (currentScroll <= headerEl.offsetTop) {
        headerEl.classList.remove('scrollUp', 'scrollDown', 'stickyZone');
        headerEl.style.height = '';
        fadeHeaderSleeveToTransparent();
      } else {
        // Toggle stickyZone and animate header sleeve background.
        if (currentScroll > headerTrigger) {
          if (!headerEl.classList.contains('stickyZone')) {
            headerEl.classList.add('stickyZone');
            fadeHeaderSleeveToWhite();
          }
        } else {
          if (headerEl.classList.contains('stickyZone')) {
            headerEl.classList.remove('stickyZone');
            fadeHeaderSleeveToTransparent();
          }
        }

        // Scrolling down: slide header down.
        if (currentScroll > lastScroll && currentScroll > headerTrigger && !headerEl.classList.contains('scrollDown')) {
          headerEl.style.height = `${headerSleeve.offsetHeight}px`;
          headerEl.classList.remove('scrollUp');
          headerEl.classList.add('scrollDown');
          if (sticky) {
            sticky.forEach(el => {
              el.style.top = `${Number(el.getAttribute('data-y-offset'))}px`;
            });
          }
        }
        // Scrolling up: slide header up.
        else if (currentScroll < lastScroll && currentScroll > headerTrigger && headerEl.classList.contains('scrollDown')) {
          headerEl.classList.remove('scrollDown');
          headerEl.classList.add('scrollUp');
          if (sticky) {
            sticky.forEach(el => {
              el.style.top = `${Number(el.getAttribute('data-y-offset')) + headerSleeve.offsetHeight}px`;
            });
          }
          if (adminBar) {
            headerSleeve.style.top = `${adminBar.offsetHeight}px`;
            if (sticky) {
              sticky.forEach(el => {
                el.style.top = `${headerSleeve.offsetHeight + adminBar.offsetHeight + Number(el.getAttribute('data-y-offset'))}px`;
              });
            }
          }
        }
      }
      lastScroll = currentScroll;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    });
  } // End sticky header


  /*************************************
   * H1 HEADER STAGGERED LINE ANIMATION
   *************************************/
  jQuery('h1').each(function() {
    const $h1 = jQuery(this);
    //if $h1 does not have a class no-reveal
    if (!$h1.hasClass('no-line-reveal')) {
      // Split text into words and wrap in spans
      const words = $h1.text().trim().split(' ');
      $h1.html(words.map(word => `<span class="word">${word}</span>`).join(' '));

      // Allow DOM update before measuring offsets
      setTimeout(() => {
        let lines = [];
        let currentLine = [];
        let lastOffsetTop = null;

        $h1.find('.word').each(function() {
          const $word = jQuery(this);
          const offsetTop = Math.round($word.offset().top);
          if (lastOffsetTop === null) {
            lastOffsetTop = offsetTop;
          }
          if (Math.abs(offsetTop - lastOffsetTop) > 5) {
            lines.push(currentLine);
            currentLine = [];
            lastOffsetTop = offsetTop;
          }
          currentLine.push(this);
        });
        if (currentLine.length) lines.push(currentLine);

        $h1.css('visibility', 'visible');
        lines.forEach(lineGroup => {
          jQuery(lineGroup).wrapAll('<span class="line"></span>');
        });
        
        // Animate each line group with a stagger
        lines.forEach((lineGroup) => {
          animate('.line', {
            translateY: [200, 0],
            duration: 1000,
            easing: 'easeOutCubic',
            delay: stagger(100),
          });
        });
      }, 400);
    }
  });
  
  /*************************************
   * REVEAL ELEMENTS ON SCROLL
   *************************************/
  let scrollTimeout;
  let $revealSlides;
  let $revealFade;
  let $revealSlideScale; // for elements that slide-up & fade in then scale on scroll

  // Check if element is in viewport
  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  window.revealElements = function() {
    $revealSlides = jQuery('.reveal-slide');
    $revealFade = jQuery('.reveal-fade');
    $revealSlideScale = jQuery('.reveal-scale');
    
    let delay = 0;
    const staggerDelay = 300; // milliseconds

    // Handle basic slide elements
    $revealSlides.each(function() {
      if (isInViewport(this) && !jQuery(this).hasClass('visible')) {
        jQuery(this)
          .css('transition-delay', `${delay}ms`)
          .addClass('visible');
          delay += staggerDelay;
          //we have to wait for the title animation to complete before adding more delays.
          if(delay == 600) {
            delay = 900 + staggerDelay;
          }
        
      }
    });
    
    // Handle fade elements
    $revealFade.each(function() {
      if (isInViewport(this) && !jQuery(this).hasClass('visible')) {
        jQuery(this)
          .css('transition-delay', `${delay}ms`)
          .addClass('visible');
        delay += staggerDelay;
      }
    });
    
    // Handle slide-scale elements:
    // • When not yet visible and in the viewport: add a delay and the "visible" class so CSS can do the slide-up and fade-in.
    // • Once visible, continuously update only the scaling based on scroll progress.
    // Updated loop for slide‑scale elements:
    const minScale   = 1;    // start at 100%
    const maxScale   = 1.2;  // end at 120%
    const scaleRange = maxScale - minScale;
    $revealSlideScale.each(function() {
      const $this = jQuery(this);
  
      // 1) Ensure .scale-content wrapper exists
      let $scaleContent = $this.find('.scale-content');
      if (!$scaleContent.length) {
        $this.wrapInner('<div class="scale-content"></div>');
        $scaleContent = $this.find('.scale-content');
        if ($this.hasClass('framed')) {
          $this.removeClass('framed');
          $scaleContent.addClass('framed');
        }
      }
  
      const rect = this.getBoundingClientRect();
  
      // 2) Immediate & continuous scaling if in view
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        let progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        progress = Math.min(Math.max(progress, 0), 1);
  
        // compute scale based on our variables
        const scaleVal = minScale + scaleRange * progress;
        $scaleContent.css('transform', `scale(${scaleVal})`);
      }
  
      // 3) Staggered slide-up & fade-in reveal
      if (!$this.hasClass('visible') && isInViewport(this)) {
        $this
          .css('transition-delay', `${delay}ms`)
          .addClass('visible');
        delay += staggerDelay;
      }
    });
  };

  const onScroll = () => {
    if (!scrollTimeout) {
      scrollTimeout = requestAnimationFrame(() => {
        revealElements();
        scrollTimeout = null;
      });
    }
  };

  // Initial reveal on load and attach optimized scroll listener
  revealElements();
  $window.on('scroll', onScroll);



  


  /*************************************
   * ACCORDION BUTTONS & NAVIGATION
   *************************************/
  const accordionButtons = document.querySelectorAll('.switch');
  if ( accordionButtons ) {
    accordionButtons.forEach(button => {
      button.addEventListener('click', e =>
        toggleAccordionSection(e, accordionCallback)
      );

      // Keydown: handle Enter/Space first, otherwise delegate to Nav
      button.addEventListener('keydown', e => {
        // Normalize Space and Enter
        if ( e.key === 'Enter' || e.key === ' ' ) {
          e.preventDefault();
          e.stopPropagation();

          // reuse the same API as click
          toggleAccordionSection(e, accordionCallback);

          // explicitly put focus back on this button
          e.currentTarget.focus();
        } else {
          // arrow/Home/End handled here
          accordionKeyNavigation(e);
        }
      });

      const labelDiv = document.getElementById(button.id + '-label');
      if ( labelDiv ) {
        labelDiv.addEventListener('click', () =>
          toggleAccordionSection({ currentTarget: button }, accordionCallback)
        );
      }
    });
  }


  function toggleAccordionSection(e,callback = () => {}) {
    const target = e.currentTarget;
    const targetPanel = document.getElementById(target.getAttribute('aria-controls'));

    if (target.getAttribute('aria-expanded') === 'false') {
      // Open accordion
      target.setAttribute('aria-expanded', 'true');
      target.classList.add('active');
      target.querySelector('span').textContent = target.getAttribute('data-hide-label');
      targetPanel.hidden = false;
      target.focus({preventScroll: true});
      /*const descEl = document.getElementById(`${target.getAttribute('id')}-description`);
      if (descEl) {
        descEl.style.opacity = 1;
        descEl.style.maxHeight = "inherit";
      }*/
      //targetPanel.style.maxHeight = targetPanel.scrollHeight + "px";
     // targetPanel.style.opacity = 1;
      animate(targetPanel, {
        //maxHeight: targetPanel.scrollHeight + "px",
        opacity: 1,
        duration: 1000,
        ease: 'out(2)',
        onComplete: () => {
          //targetPanel.style.overflow = "visible";
        }
      });
      revealElements(); // also animate expert cards if any
    } else {
      // Close accordion
      //targetPanel.style.overflow = "hidden";
      target.setAttribute('aria-expanded', 'false');
      target.classList.remove('active');
      target.querySelector('span').textContent = target.getAttribute('data-show-label');
      /*const descEl = document.getElementById(`${target.getAttribute('id')}-description`);
      if (descEl) {
        descEl.style.opacity = 0;
        descEl.style.maxHeight = "0px";
      }*/
      // targetPanel.style.maxHeight = "0px";
      //targetPanel.style.opacity = 0;
      /*targetPanel.addEventListener('transitionend', function handler() {
        targetPanel.hidden = true;
      }, { once: true });*/
      animate(targetPanel, {
        //maxHeight: "0px",
        opacity: 0,
        duration: 400,
        ease: 'out(3)',
        onComplete: () => {
          targetPanel.hidden = true;
          //targetPanel.style.overflow = "hidden";
        }
      });
    }
    if (typeof callback === 'function') {
      callback(target,targetPanel);
    }
  }

  //accessibility keyboard nav
  function accordionKeyNavigation(e) {
    const key = e.key;
    const currentIndex = Array.prototype.indexOf.call(accordionButtons, e.currentTarget);
    let newIndex;
    switch (key) {
      case 'ArrowDown':
        newIndex = (currentIndex + 1) % accordionButtons.length;
        accordionButtons[newIndex].focus();
        e.preventDefault();
        break;
      case 'ArrowUp':
        newIndex = (currentIndex - 1 + accordionButtons.length) % accordionButtons.length;
        accordionButtons[newIndex].focus();
        e.preventDefault();
        break;
      case 'Home':
        accordionButtons[0].focus();
        e.preventDefault();
        break;
      case 'End':
        accordionButtons[accordionButtons.length - 1].focus();
        e.preventDefault();
        break;
      default:
        break;
    }
  }

  /*************************************
   * CITY TOGGLES (Office Listing)
   *************************************/
  const cityToggles = document.querySelectorAll('.city-toggle button');
  if ( cityToggles ) {
    cityToggles.forEach(cityToggle => {
      cityToggle.addEventListener('click', toggleCity);

      // Keep focus on Enter
      cityToggle.addEventListener('keydown', e => {
        if ( e.key === 'Enter' ) {
          e.preventDefault();
          e.stopPropagation();
          toggleCity(e);
          cityToggle.focus();
        }
      });

      // Initial collapsed state
      const targetPanel = document.getElementById(
        cityToggle.getAttribute('aria-controls')
      );
      if ( targetPanel ) {
        targetPanel.style.maxHeight = '0px';
        targetPanel.style.opacity   = 0;
      }

      // Click proxy on label div
      const labelDiv = document.getElementById(
        `${cityToggle.id}-label`
      );
      if ( labelDiv ) {
        labelDiv.addEventListener('click', () =>
          toggleCity({ currentTarget: cityToggle })
        );
      }
    });
  }


  function toggleCity(e) {
    const target = e.currentTarget;
    const targetPanel = document.getElementById(target.getAttribute('aria-controls'));
    const parentPanelEl = document.getElementById(target.getAttribute('data-parent-panel'));
    if (parentPanelEl) {
      parentPanelEl.style.maxHeight = "inherit";
    }
    if (target.getAttribute('aria-expanded') === 'false') {
      target.setAttribute('aria-expanded', 'true');
      target.classList.add('active');
      target.querySelector('span').textContent = target.getAttribute('data-hide-label') + ' ' + target.getAttribute('data-label-append');
      if ( 'inert' in targetPanel ) targetPanel.inert = false;
      targetPanel.removeAttribute('aria-hidden');
      targetPanel.style.maxHeight = targetPanel.scrollHeight + "px";
      targetPanel.style.opacity = 1;
      revealElements();
    } else {
      target.setAttribute('aria-expanded', 'false');
      target.classList.remove('active');
      target.querySelector('span').textContent = target.getAttribute('data-show-label') + ' ' + target.getAttribute('data-label-append');
      if ( 'inert' in targetPanel ) targetPanel.inert = true;
      targetPanel.setAttribute('aria-hidden','true');
      targetPanel.style.maxHeight = "0px";
      targetPanel.style.opacity = 0;
      
    }
  }

  /*************************************
   * REVEAL ALL SWITCHES
   *************************************/
  if (toggleAllSwitches) {
    toggleAllSwitches.forEach(switchEl => {
      const toggleInput = switchEl.querySelector('.toggle input[type="checkbox"]');
      const labelSpan = switchEl.querySelector('span.label');
      // Re-cache accordion buttons inside the switch handler if needed.
      const accordionButtons = document.querySelectorAll('.accordion button');
      toggleInput.addEventListener('change', function() {
        if (toggleInput.checked) {
          labelSpan.textContent = toggleInput.getAttribute('data-hide-label');
          toggleInput.setAttribute('aria-checked', 'true');
          toggleInput.setAttribute('aria-label', toggleInput.getAttribute('data-hide-label'));
          accordionButtons.forEach(button => {
            if (button.getAttribute('aria-expanded') === 'false') {
              button.click();
            }
          });
        } else {
          labelSpan.textContent = toggleInput.getAttribute('data-show-label');
          toggleInput.setAttribute('aria-checked', 'false');
          toggleInput.setAttribute('aria-label', toggleInput.getAttribute('data-show-label'));
          accordionButtons.forEach(button => {
            if (button.getAttribute('aria-expanded') !== 'false') {
              button.click();
            }
          });
        }
      });
      //accessibility keyboard nav
      toggleInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
      
          // toggle checked state
          toggleInput.checked = !toggleInput.checked;
      
          // fire the change event
          toggleInput.dispatchEvent(new Event('change', { bubbles: true }));
      
          // explicitly keep focus on the switch
          toggleInput.focus();
        }
      });
    });
  }

  /*************************************
   * TABS FUNCTIONALITY (Experts/Work Pages)
   *************************************/
  if (tabs) {
    tabs.forEach(tab => {
      if (ourExpertsTabs.length > 0) {
        tab.addEventListener('click', (e) => changeTabs(e, tabsCallback));
      } else {
          tab.addEventListener('click', changeTabs);
      }
      tab.addEventListener('keydown', keyNavigation);
    });
  }

  function changeTabs(e, callback = null) {
    e.preventDefault();
    const target = e.currentTarget;
    const targetPanel = document.getElementById(target.getAttribute('aria-controls'));
	//if we have expert tabs, they need to toggle on and off
	if( ourExpertsTabs.length > 0 && target.getAttribute('aria-selected') === 'true' ) {
		target.setAttribute('aria-selected', 'false');
		target.classList.remove('active');
		targetPanel.hidden = true;
		targetPanel.classList.remove('active');
		tabs.forEach(tab => {
			if (window.innerWidth >= mobileWidth) {
				tab.classList.remove('active');
			}
		});
	} else {
		// Hide all tabs and panels
		tabs.forEach(tab => {
			tab.setAttribute('aria-selected', 'false');
			//if (window.innerWidth >= mobileWidth) {
        tab.classList.remove('active');
      //}
			const panel = document.getElementById(tab.getAttribute('aria-controls'));
			panel.hidden = true;
			panel.classList.remove('active');
		});

		// Activate clicked tab and panel
		target.setAttribute('aria-selected', 'true');
		target.classList.add('active');
		target.classList.remove('inactive');
		targetPanel.hidden = false;
		targetPanel.classList.add('active');
		target.focus();

		// Scroll to panel, offset by tabs container height
		setTimeout(function() {
			const panelTop = targetPanel.getBoundingClientRect().top + window.pageYOffset;
      let tabsOffset = 0;
      if(headerEl.classList.contains('scrollUp')) {
        tabsOffset = bannerSleeveHeight;
      } 
			window.scrollTo({
				top: panelTop - tabsContainer.offsetHeight - tabsOffset, //TODO, replace 110 with calculated 
				behavior: 'smooth'
			});
		}, 200);

		// Reset toggle switches and related reveals if present
		if (toggleAllSwitches) {
		toggleAllSwitches.forEach(switchEl => {
			const toggleInput = switchEl.querySelector('.toggle input[type="checkbox"]');
			if (toggleInput.checked) {
			toggleInput.checked = false;
			toggleInput.dispatchEvent(new Event('change'));
			}
		});
		jQuery('.reveal-fade').removeClass('visible');
		revealElements();
		}
	} //end if expertTabs

	if (typeof callback === 'function') {
		callback(target, target.getAttribute('aria-controls'));
	}

  }

  function keyNavigation(e) {
    const key = e.key;
    const currentIndex = Array.prototype.indexOf.call(tabs, e.currentTarget);
    let newIndex;
    if (key === 'ArrowRight') {
      newIndex = (currentIndex + 1) % tabs.length;
      tabs[newIndex].focus();
    } else if (key === 'ArrowLeft') {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      tabs[newIndex].focus();
    }
  }

  /*************************************
   * DEEP LINKING & SCROLL UTILITIES
   *************************************/
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const scrollAndClick = async (elementId, click = false, wait = 300) => {
    const elem = document.getElementById(elementId);
    if (elem) {
      elem.scrollIntoView({ behavior: "instant" });
      await delay(wait);
      if (click && (elem.tagName.toLowerCase() === "button" || elem.type === "button")) {
        elem.click();
      }
    }
  };

  // Handle URL query and hash parameters for deep linking
  const urlParams = new URLSearchParams(window.location.search);
  const cleanHash = window.location.hash ? window.location.hash.substring(1) : "";
  
  // Split the pathname into segments and remove empty values.
  const url = new URL(window.location.href);
  const segments = url.pathname.split("/").filter(seg => seg.length > 0);
  // If there are at least two segments, assume the last segment is the location value.
  const locationValue = segments.length >= 2 ? segments[segments.length - 1] : "";
  

  if (urlParams.has("tab")) {
    scrollAndClick("tabs");
    scrollAndClick("sectors-tab", true, 300);
    if (cleanHash) {
      delay(600).then(() => scrollAndClick(cleanHash, true, 0));
    }
  } else if ((urlParams.has("location") && cleanHash) || (locationValue && cleanHash)) {
    //console.log(locationValue + " = " + cleanHash);
    scrollAndClick(cleanHash, true, 600);
    //if we're not accessing the parent, but an actual office location
    if(locationValue != cleanHash) { 
      delay(2000).then(() => {
        let locationElement;
        if(urlParams.has("location")) {
          locationElement = document.getElementById(urlParams.get("location"));
        } else if(locationValue) {
          locationElement = document.getElementById(locationValue);
        }
        if (locationElement && (locationElement.tagName.toLowerCase() === "button" || locationElement.type === "button")) {
          locationElement.click();
          locationElement.scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  } else if (cleanHash) {
    //console.log(cleanHash);
    const fallbackButton = document.getElementById(cleanHash);
    if (fallbackButton && fallbackButton.type === "button") {
      fallbackButton.click();
    }
  }

  /*************************************
   * PAGE LOAD fade in/out transitions
   *************************************/
  // Use the pageshow event to handle fade-in even from bfcache
  /*window.addEventListener("pageshow", function (event) {
    const navigationEntries = performance.getEntriesByType("navigation");
    const isBackForward = navigationEntries.length && navigationEntries[0].type === "back_forward";

    if (event.persisted || isBackForward) {
      // Force a reflow for cached pages
      document.documentElement.style.display = "none";
      requestAnimationFrame(function () {
        document.documentElement.style.display = "";
        document.documentElement.classList.add("fade-in");
      });
    } else {
      document.documentElement.classList.add("fade-in");
    }
  });*/

  /*************************************
   * CITIES LIST 
   *************************************/
  function updateDisplayByLine(container) {
    // Select only inline elements with the class 'bullet'
    /*const elements = Array.from(container.querySelectorAll('.bullet')).filter(el => {
      return window.getComputedStyle(el).display.includes('inline');
    });*/
    const elements = container.querySelectorAll('.bullet');
  
    // Group elements by line using the top coordinate
    const lines = {};
    elements.forEach(el => {
      const rects = el.getClientRects();
      if (rects.length) {
        // Use the first rectangle's top value (rounded to handle subpixel differences)
        const topKey = Math.round(rects[0].top);
        if (!lines[topKey]) {
          lines[topKey] = [];
        }
        lines[topKey].push({ el, rect: rects[0] });
      }
    });
    
   
    // Process each line group
    Object.values(lines).forEach(line => {
      // Sort elements by their left coordinate to determine order on the line
      line.sort((a, b) => a.rect.left - b.rect.left);
  
      // For each element on the line, hide the first bullet, show the rest.
      line.forEach((item, index) => {
        if (index === 0) {
          //item.el.style.visibility = 'hidden';
          item.el.classList.add('removed');
        } else {
          //item.el.style.visibility = 'visible';
          item.el.classList.remove('removed');
        }
      });
    });
  }
  

  /*************************************
   * RESIZE LOGIC 
   *************************************/
  // Trigger on load if cities list present
  const cities = document.querySelector('.global-network-locations');
  if (cities) {
    //delay the initial call to updateDisplayByLine
    // to allow for the page to load and the css to be applied
    setTimeout(() => {
      updateDisplayByLine(cities);
    }
    , 400);
    // Create a debounced version of updateDisplayByLine
    const debouncedupdateDisplayByLine = debounce(updateDisplayByLine, 250);
    // Attach the handler to the resize event
   // Wrap the debounced function to pass a custom argument
    window.addEventListener('resize', () => {
      debouncedupdateDisplayByLine(cities);
    });
  }

  /*if(workAccordions.length > 0) {
    //loop through elements in the workAccordions, find any buttons with class `active` get the element id from its `aria-controls` attribute and set the max height of that element to that elements scrollheight
    function updateAccordionHeight() {
      workAccordions.forEach(workAccordion => {
        const activeButtons = workAccordion.querySelectorAll('button.active');
        if (activeButtons.length > 0) {
          activeButtons.forEach(activeButton => {
            const panel = document.getElementById(activeButton.getAttribute('aria-controls'));
            if (panel) {
              panel.style.maxHeight = `${panel.scrollHeight}px`;
            }
          });
        }
      });
    }
    // Create a debounced version of updateAccordionHeight
    const debouncedupdateAccordionHeight = debounce(updateAccordionHeight, 250);
    // Attach the handler to the resize event
    // Wrap the debounced function to pass a custom argument if needed
    window.addEventListener('resize', () => {
      debouncedupdateAccordionHeight();
    });
  }*/


});
